/**
 * Sign in and register — the design's "Sign in" screen, which is one screen with two modes.
 *
 * Re-derived from `design/Athena Dashboard.html` driven in a browser, which is the first export
 * that renders these two screens at all. What that showed, and what is reproduced here:
 *
 *   - A two-pane dark split: the form on the left, a marketing aside on the right.
 *   - The aside carries a mock agent terminal that cycles through four sessions on a timer.
 *   - Errors render in a boxed panel between the lede and the first field, not beside a field.
 *   - Both modes offer a way back out to the public site.
 *
 * One deliberate difference from the design: its register field is captioned "at least 8
 * characters". `RegisterRequest.password` is `Field(min_length=12)`, so the server would reject
 * a password the design invites. The screen says 12.
 */

import { useEffect, useState, type FormEvent } from "react";

import { ApiError } from "../lib/api/errors";
import { useAuth } from "../lib/auth/auth-context";

export type AuthMode = "sign-in" | "register";

/** `RegisterRequest.password` is `Field(min_length=12)`. Said before the server has to say it. */
const MIN_PASSWORD_LENGTH = 12;

/**
 * The public site. The export links here and labels the link with the same string, so the two are
 * kept in step: fixing only the target would leave the screen printing a different address than it
 * navigates to.
 *
 * This briefly diverged from the design. The export used to link to `engineering-athena.dev`, a
 * domain with no DNS record at all, so the link had never worked for anyone who clicked it; the
 * code was corrected here first and the export caught up. They agree now, and the test below the
 * fold in `app.test.tsx` pins the working domain so a future export cannot quietly undo it.
 *
 * A constant rather than a `VITE_*` variable: this does not vary per environment, and adding one
 * would mean touching the Dockerfile build args and the CD workflow for nothing.
 * `apps/web/src/lib/site.ts` defaults its `dashboardUrl` the same way.
 */
const MARKETING_SITE = "https://engineeringathena.com";
const MARKETING_SITE_LABEL = "engineeringathena.com";

/**
 * The aside's rotating agent session. Four examples, verbatim from the design.
 *
 * Each one is a prompt, the `athena_*` call it resolves to, two lines of what came back, and a
 * provenance line. `tone` is the colour the design gives that provenance line, which is the only
 * thing distinguishing the four kinds — so the word ("flow", "guide", "rule", "knowledge") is
 * written out in the line itself and the colour never carries the meaning alone.
 *
 * Every line names a *title* or a general source. No corpus path, folder or page number appears in
 * any of them, which is what ADR-0010 asks of anything user-facing.
 */
const DEMO_SESSIONS = [
  {
    prompt: "release svc-billing to prod",
    call: 'athena_flow("safe release")',
    returned: ["Safe release checklist · 6 steps", "rollback = re-promote previous image"],
    meta: "flow · from the Athena Handbook",
    tone: "flow",
  },
  {
    prompt: "add auth to this django app",
    call: 'athena_guide("auth in django")',
    returned: ["JWT with djoser · step by step", "working code · the agent keeps what fits"],
    meta: "guide · from the Athena Handbook",
    tone: "guide",
  },
  {
    prompt: "how should I handle errors here?",
    call: 'athena_rules("error handling")',
    returned: ["Error handling done right · 4 graded rules", "MUST no bare except · MUST typed errors"],
    meta: "rule · from the Athena Handbook",
    tone: "rule",
  },
  {
    prompt: "should this queue be SQS or Kafka?",
    call: 'athena_knowledge("queue choice")',
    returned: ["Choosing between SQS and Kafka", "deep research · added by your team"],
    meta: "knowledge · from your workspace",
    tone: "knowledge",
  },
] as const;

const DEMO_INTERVAL_MS = 4500;

/** The four points beside the form, verbatim from the design. */
const ASIDE_POINTS = [
  "One command connects Claude Code, Cursor or Codex",
  "Works from day one — the reviewed Handbook is included",
  "Missing something? Add your own — it’s served the same way",
  "Read-only, user scope — nothing lands in your repos",
] as const;

/**
 * Cycles the session, unless the visitor asked for less motion — in which case the first session
 * simply stays. `prefers-reduced-motion` is honoured in JS as well as CSS because a swap on a
 * timer is motion even when the fade is disabled.
 *
 * The design stacks all four panes absolutely and cross-fades them with one `authcycle` keyframe
 * per pane. One pane at a time is rendered here instead: it reads the same, it does not overlap
 * two transcripts mid-fade, and it is what lets the rotation actually stop under reduced motion
 * rather than merely animating instantly.
 */
function useRotatingCard(count: number): number {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const query = globalThis.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (query?.matches === true) return;

    const id = setInterval(() => {
      setIndex((current) => (current + 1) % count);
    }, DEMO_INTERVAL_MS);
    return () => clearInterval(id);
  }, [count]);

  return index;
}

interface Props {
  mode: AuthMode;
  onSwitchMode: (mode: AuthMode) => void;
}

export function AuthScreen({ mode, onSwitchMode }: Props) {
  const auth = useAuth();
  const isRegister = mode === "register";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const cardIndex = useRotatingCard(DEMO_SESSIONS.length);
  // The index is always in range, but a number index into a tuple is `T | undefined` under
  // noUncheckedIndexedAccess, and the first session is a fine thing to fall back to.
  const session = DEMO_SESSIONS[cardIndex] ?? DEMO_SESSIONS[0];

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);

    if (isRegister && password !== confirm) {
      setError("The two passwords do not match.");
      return;
    }
    if (isRegister && password.length < MIN_PASSWORD_LENGTH) {
      setError(`Choose a password of at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }

    setBusy(true);
    try {
      if (isRegister) {
        await auth.register(email, password);
      } else {
        await auth.signIn(email, password);
      }
      // On success the router's guard moves us; this component unmounts.
    } catch (caught) {
      // The service writes better messages than this app could. "email or password is incorrect",
      // "an account with that email already exists" — shown as written. See lib/api/errors.ts.
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Something went wrong that this app did not expect. Try again.",
      );
      setBusy(false);
    }
  }

  function switchMode() {
    setError(null);
    setConfirm("");
    onSwitchMode(isRegister ? "sign-in" : "register");
  }

  return (
    <div className="auth">
      <div className="auth__form-side">
        <form className="auth__form" onSubmit={handleSubmit} noValidate>
          <p className="auth__brand">Engineering Athena</p>
          <h1 className="auth__title">{isRegister ? "Create your workspace" : "Sign in"}</h1>
          <p className="auth__lede">Working knowledge for your coding assistant — on demand.</p>

          {error !== null && (
            <div className="auth__error" role="alert">
              <p>{error}</p>
            </div>
          )}

          <label className="auth__label" htmlFor="auth-email">
            Email
          </label>
          <input
            id="auth-email"
            className="auth__input"
            type="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="you@company.com"
            autoComplete="email"
            autoCapitalize="off"
            spellCheck={false}
          />

          <label className="auth__label" htmlFor="auth-password">
            Password
          </label>
          <input
            id="auth-password"
            className="auth__input"
            type="password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete={isRegister ? "new-password" : "current-password"}
            aria-describedby={isRegister ? "auth-password-hint" : undefined}
          />
          {isRegister && (
            <p className="auth__hint" id="auth-password-hint">
              At least {MIN_PASSWORD_LENGTH} characters.
            </p>
          )}

          {isRegister && (
            <>
              <label className="auth__label" htmlFor="auth-confirm">
                Confirm password
              </label>
              <input
                id="auth-confirm"
                className="auth__input"
                type="password"
                required
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
                autoComplete="new-password"
              />
            </>
          )}

          <button className="auth__submit" type="submit" disabled={busy}>
            {busy
              ? isRegister
                ? "Creating…"
                : "Signing in…"
              : isRegister
                ? "Create account"
                : "Sign in"}
          </button>

          <p className="auth__switch">
            {isRegister ? "Already have a workspace?" : "New to Athena?"}{" "}
            <button type="button" className="auth__link-button" onClick={switchMode}>
              {isRegister ? "Sign in" : "Create an account"}
            </button>
          </p>

          <p className="auth__back">
            <a href={MARKETING_SITE}>← Back to {MARKETING_SITE_LABEL}</a>
          </p>
        </form>
      </div>

      {/*
        Decorative. It is a restatement of the public site's pitch, and a screen reader that
        skipped it would lose nothing that the form does not already say.
      */}
      <div className="auth__aside" aria-hidden="true">
        <div className="auth__aside-rings" />

        <div className="auth__aside-copy">
          <p className="auth__aside-eyebrow">Intelligence on demand</p>
          <p className="auth__aside-headline">
            Whatever you&rsquo;re trying to get done, your assistant gets the working knowledge for
            it.
          </p>
          <p className="auth__aside-body">
            A task, a setup, a best practice — the intelligence comes built in, from a handbook we
            write and review. And when something is true only of your team, you add it yourself.
          </p>

          <div className="agent-window">
            <div className="agent-window__bar">
              <span className="agent-window__dot" />
              <span className="agent-window__dot" />
              <span className="agent-window__dot" />
              <span className="agent-window__title">coding agent — athena mcp</span>
              <span className="agent-window__status">
                <span className="agent-window__pip" />
                connected
              </span>
            </div>
            <div className="agent-window__body">
              <div className="agent-session" key={cardIndex}>
                <p className="agent-session__prompt">
                  <span className="agent-session__caret">&gt;</span> {session.prompt}
                </p>
                <p className="agent-session__call">→ {session.call}</p>
                {session.returned.map((line) => (
                  <p className="agent-session__returned" key={line}>
                    {line}
                  </p>
                ))}
                <p className="agent-session__meta" data-tone={session.tone}>
                  {session.meta}
                  <span className="agent-session__cursor" />
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="auth__aside-foot">
          <ul className="auth__points">
            {ASIDE_POINTS.map((point) => (
              <li key={point}>{point}</li>
            ))}
          </ul>
          <p className="auth__aside-note">read-only · user scope · 0 files in your repo</p>
        </div>
      </div>
    </div>
  );
}
